import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { vendorsApi } from '../api/api';

const EMPTY = { vendor_code:'', name:'', contact_person:'', email:'', phone:'', address:'', city:'', state:'', gstin:'', payment_terms:'Net 30', status:'active' };
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];

export default function VendorMaster() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ['vendors', search, statusFilter],
    queryFn: () => vendorsApi.getAll({ search, status: statusFilter }),
  });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? vendorsApi.update(editing.id, d) : vendorsApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['vendors']); qc.invalidateQueries(['dashboard']); toast.success(editing ? 'Vendor updated' : 'Vendor created'); closeModal(); },
    onError: (e) => toast.error(e.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: vendorsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['vendors']); toast.success('Vendor deleted'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (v) => { setEditing(v); setForm({...v}); setModal(true); };
  const closeModal = () => { setModal(false); setEditing(null); };
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = (e) => { e.preventDefault(); saveMutation.mutate(form); };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Vendor Master</span>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Vendor</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <Truck size={15} />
            <span className="card-title">Vendors ({vendors.length})</span>
            <div className="search-wrap">
              <Search /><input placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr>
                  <th>Code</th><th>Name</th><th>Contact</th><th>Phone</th><th>City</th><th>State</th><th>GSTIN</th><th>Payment Terms</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {vendors.length === 0 && <tr><td colSpan={10}><div className="empty-state"><Truck /><p>No vendors found</p></div></td></tr>}
                  {vendors.map(v => (
                    <tr key={v.id}>
                      <td className="mono">{v.vendor_code}</td>
                      <td style={{fontWeight:500}}>{v.name}</td>
                      <td>{v.contact_person || '—'}</td>
                      <td>{v.phone || '—'}</td>
                      <td>{v.city || '—'}</td>
                      <td>{v.state || '—'}</td>
                      <td className="mono">{v.gstin || '—'}</td>
                      <td>{v.payment_terms || '—'}</td>
                      <td><span className={`badge badge-${v.status}`}>{v.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-icon" onClick={() => openEdit(v)}><Pencil /></button>
                          {/* <button className="btn-icon danger" onClick={() => { if(window.confirm('Delete this vendor?')) deleteMutation.mutate(v.id); }}><Trash2 /></button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Vendor' : 'Add New Vendor'}</h2>
              <button className="btn btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Vendor Code *</label>
                    <input className="form-control" required value={form.vendor_code} onChange={e => set('vendor_code', e.target.value)} placeholder="e.g. VND-001" disabled={!!editing} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company Name *</label>
                    <input className="form-control" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Vendor company name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Person</label>
                    <input className="form-control" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} placeholder="Name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="10-digit mobile" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GSTIN</label>
                    <input className="form-control" value={form.gstin} onChange={e => set('gstin', e.target.value)} placeholder="15-char GST number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <select className="form-control" value={form.state} onChange={e => set('state', e.target.value)}>
                      <option value="">Select state</option>
                      {STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Terms</label>
                    <select className="form-control" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)}>
                      <option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Net 60</option><option>Immediate</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group form-full">
                    <label className="form-label">Address</label>
                    <textarea className="form-control" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full address" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : editing ? 'Update Vendor' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}